/**
 * Parallax Animation Module
 * Handles scroll-based parallax effects
 * 
 * Classes:
 * - .p-up          : Moves upward on scroll
 * - .p-up-fast     : Moves upward faster on scroll
 * - .p-down        : Moves downward on scroll
 * - .p-down-fast   : Moves downward faster on scroll
 * - .p-up-left     : Moves up and left on scroll (activates when in viewport)
 * 
 * Uses CSS custom properties to combine with entrance effects:
 * - --parallax-x : Horizontal parallax offset
 * - --parallax-y : Vertical parallax offset
 */

'use strict';

// =============================================================================
// CONFIGURATION - Edit these values to adjust animation behavior
// =============================================================================

const CONFIG = {
	// Base speed multiplier (higher = more movement)
	speed: 0.05,
	speedFast: 0.1,
	
	// Maximum movement in pixels (prevents extreme offsets)
	maxMovement: 50,
	maxMovementFast: 80,
	
	// Diagonal movement settings (p-up-left, etc.)
	diagonalSpeed: 0.12,
	maxDiagonalMovement: 150,
};

// =============================================================================
// SHARED TRANSFORM HELPER
// =============================================================================

/**
 * Apply combined transform using CSS custom properties
 * Combines entrance effects (--entrance-x, --entrance-y) with parallax (--parallax-x, --parallax-y)
 */
const applyTransform = (element) => {
	const entranceX = parseFloat(element.style.getPropertyValue('--entrance-x')) || 0;
	const entranceY = parseFloat(element.style.getPropertyValue('--entrance-y')) || 0;
	const parallaxX = parseFloat(element.style.getPropertyValue('--parallax-x')) || 0;
	const parallaxY = parseFloat(element.style.getPropertyValue('--parallax-y')) || 0;
	
	element.style.transform = `translate(${entranceX + parallaxX}px, ${entranceY + parallaxY}px)`;
};

// =============================================================================
// ENTRANCE CLASS DETECTION
// =============================================================================

const ENTRANCE_CLASSES = ['e-in-from-left', 'e-in-from-right', 'e-fade-in-up'];

/**
 * Check if element has an entrance effect class
 */
const hasEntranceEffect = (element) => {
	return ENTRANCE_CLASSES.some(cls => element.classList.contains(cls));
};

/**
 * Check if entrance animation is complete
 */
const isEntranceComplete = (element) => {
	return element.dataset.entranceComplete === 'true';
};

// =============================================================================
// STANDARD PARALLAX (continuous scroll-based movement from page load)
// =============================================================================

const PARALLAX_CLASSES = [
	'.p-up',
	'.p-up-fast', 
	'.p-down',
	'.p-down-fast'
];

const parallaxElements = document.querySelectorAll(PARALLAX_CLASSES.join(', '));

if (parallaxElements.length > 0) {
	let ticking = false;
	
	// Track element data including activation state for elements with entrance effects
	const elementData = new Map();
	
	/**
	 * Get configuration for a specific element based on its classes
	 */
	const getElementConfig = (element) => {
		const isFast = element.classList.contains('p-up-fast') || element.classList.contains('p-down-fast');
		const isUp = element.classList.contains('p-up') || element.classList.contains('p-up-fast');
		
		return {
			speed: isFast ? CONFIG.speedFast : CONFIG.speed,
			maxMovement: isFast ? CONFIG.maxMovementFast : CONFIG.maxMovement,
			directionY: isUp ? 1 : -1,
		};
	};
	
	/**
	 * Initialize element data
	 */
	const initElementData = (element) => {
		const currentTransform = element.style.transform;
		element.style.transform = '';
		const rect = element.getBoundingClientRect();
		element.style.transform = currentTransform;
		
		const hasEntrance = hasEntranceEffect(element);
		
		elementData.set(element, {
			top: rect.top + window.scrollY,
			height: rect.height,
			config: getElementConfig(element),
			hasEntrance: hasEntrance,
			parallaxActive: !hasEntrance, // Active immediately if no entrance effect
			activationScrollY: hasEntrance ? null : (window.scrollY || window.pageYOffset),
		});
	};
	
	// Initialize all elements
	parallaxElements.forEach(initElementData);
	
	/**
	 * Apply parallax transforms based on scroll position
	 */
	const handleParallax = () => {
		const scrollY = window.scrollY || window.pageYOffset;
		const windowHeight = window.innerHeight;
		const viewportCenter = scrollY + windowHeight / 2;
		
		parallaxElements.forEach((element) => {
			const data = elementData.get(element);
			if (!data) return;
			
			// Check if this element has entrance effect and if it's complete
			if (data.hasEntrance && !data.parallaxActive) {
				if (isEntranceComplete(element)) {
					// Entrance just completed - activate parallax from current position
					data.parallaxActive = true;
					data.activationScrollY = scrollY;
				} else {
					// Entrance not complete yet - skip parallax
					return;
				}
			}
			
			const { config } = data;
			
			// Calculate element center
			const elementCenter = data.top + data.height / 2;
			
			// For elements with entrance effects, calculate relative to activation point
			let distanceFromCenter;
			if (data.hasEntrance && data.activationScrollY !== null) {
				// Calculate movement relative to when parallax was activated
				const scrollDelta = scrollY - data.activationScrollY;
				distanceFromCenter = -scrollDelta; // Negative because we want movement relative to activation
			} else {
				// Standard calculation for elements without entrance effects
				distanceFromCenter = elementCenter - viewportCenter;
			}
			
			// Calculate vertical offset
			const offsetY = distanceFromCenter * config.speed * config.directionY;
			const translateY = Math.max(-config.maxMovement, Math.min(config.maxMovement, offsetY));
			
			// Set CSS custom property and apply combined transform
			element.style.setProperty('--parallax-y', translateY);
			applyTransform(element);
		});
		
		ticking = false;
	};
	
	/**
	 * Throttled scroll handler using requestAnimationFrame
	 */
	const onScroll = () => {
		if (!ticking) {
			window.requestAnimationFrame(handleParallax);
			ticking = true;
		}
	};
	
	/**
	 * Recalculate positions on resize
	 */
	const onResize = () => {
		parallaxElements.forEach((element) => {
			element.style.setProperty('--parallax-y', 0);
			applyTransform(element);
			
			// Reinitialize element data
			initElementData(element);
		});
		handleParallax();
	};
	
	// Initialize
	handleParallax();
	
	window.addEventListener('scroll', onScroll, { passive: true });
	window.addEventListener('resize', onResize, { passive: true });
}

// =============================================================================
// DIAGONAL PARALLAX (starts at original position, moves when in viewport)
// =============================================================================

const DIAGONAL_CLASSES = [
	'.p-up-left'
];

const diagonalElements = document.querySelectorAll(DIAGONAL_CLASSES.join(', '));

if (diagonalElements.length > 0) {
	let ticking = false;
	
	// Track each element's activation state and scroll position when first seen
	const elementData = new Map();
	
	/**
	 * Initialize element tracking data
	 */
	const initElementData = (element) => {
		if (elementData.has(element)) return;
		
		const rect = element.getBoundingClientRect();
		elementData.set(element, {
			isActive: false,
			activationScrollY: 0,
			top: rect.top + window.scrollY,
			height: rect.height
		});
	};
	
	// Initialize all elements
	diagonalElements.forEach(initElementData);
	
	/**
	 * Apply diagonal parallax transforms
	 */
	const handleDiagonalParallax = () => {
		const scrollY = window.scrollY || window.pageYOffset;
		
		diagonalElements.forEach((element) => {
			const data = elementData.get(element);
			if (!data || !data.isActive) return;
			
			// Calculate how far we've scrolled since activation
			const scrollDelta = scrollY - data.activationScrollY;
			
			// Only move when scrolling down (positive delta)
			const movement = Math.max(0, scrollDelta);
			
			// Calculate offset with speed multiplier
			const offset = movement * CONFIG.diagonalSpeed;
			
			// Clamp to max movement
			const clampedOffset = Math.min(offset, CONFIG.maxDiagonalMovement);
			
			// 45° angle: equal X and Y movement (negative = up and left)
			const translateX = -clampedOffset;
			const translateY = -clampedOffset;
			
			// Set CSS custom properties and apply combined transform
			element.style.setProperty('--parallax-x', translateX);
			element.style.setProperty('--parallax-y', translateY);
			applyTransform(element);
		});
		
		ticking = false;
	};
	
	/**
	 * Throttled scroll handler
	 */
	const onScroll = () => {
		if (!ticking) {
			window.requestAnimationFrame(handleDiagonalParallax);
			ticking = true;
		}
	};
	
	/**
	 * Recalculate on resize
	 */
	const onResize = () => {
		diagonalElements.forEach((element) => {
			element.style.setProperty('--parallax-x', 0);
			element.style.setProperty('--parallax-y', 0);
			applyTransform(element);
			const data = elementData.get(element);
			if (data) {
				const rect = element.getBoundingClientRect();
				data.top = rect.top + window.scrollY;
				data.height = rect.height;
				// Reset activation if element is now out of view
				if (rect.bottom < 0 || rect.top > window.innerHeight) {
					data.isActive = false;
				}
			}
		});
		handleDiagonalParallax();
	};
	
	// Use IntersectionObserver to detect when elements enter viewport
	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			const data = elementData.get(entry.target);
			if (!data) return;
			
			if (entry.isIntersecting && !data.isActive) {
				// Element just entered viewport - record scroll position
				data.isActive = true;
				data.activationScrollY = window.scrollY || window.pageYOffset;
			}
		});
	}, {
		threshold: 0,
		rootMargin: '0px'
	});
	
	// Observe all diagonal elements
	diagonalElements.forEach((element) => {
		observer.observe(element);
	});
	
	window.addEventListener('scroll', onScroll, { passive: true });
	window.addEventListener('resize', onResize, { passive: true });
}
